// usePointerMode などからの「ユーザー操作」を CanvasOp へ変換し、
// 楽観更新 (applyOptimistic) → realtime クライアント送信を一括で行う dispatcher。

import { useCallback, useMemo, useRef } from "react";
import { useShowToast } from "./notice/use-show-toast";
import {
  CANVAS_OBJECT_HARD_LIMIT,
  CANVAS_OBJECT_SOFT_LIMIT,
  clampCanvasObjectToCanvas,
  clampPointToCanvas,
  computePenStrokeFrame,
  normalizeCanvasSizePreset,
  COMMENT_MESSAGES_MAX,
  type CanvasOp,
  type CanvasObject,
  type CanvasSizePreset,
  type CommentMessage,
  type PenStrokeCanvasObject,
  type StickyCanvasObject,
  type TextCanvasObject,
  type ShapeCanvasObject,
  type ImageCanvasObject,
  type VideoCanvasObject,
  type LinkCardCanvasObject,
  type ConnectorEndpoint,
  type ConnectorFrame,
  connectorStoredFrame,
  collectConnectorCascadeDeleteIds,
  SECTION_DEFAULT_FILL,
  clampZOrderForPersist,
  type TransformObjectOp,
} from "@refinement-canvas/domain";

// コメントスレッドオブジェクトの保存上の名目サイズ（ステージ px）。
// ピンはビューポート固定サイズのオーバーレイ層で描画するため、x/y をアンカー（クリック位置）とし、
// w/h は範囲内 clamp 用の最小フットプリントとしてのみ使う。
export const COMMENT_THREAD_NOMINAL_SIZE = 1;
import { customAlphabet } from "nanoid";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useRealtimeClient } from "../../realtime/realtime-context";

const generateId = customAlphabet("23456789abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ", 16);

interface DispatcherContext {
  readonly actorUserId: string;
  /**
   * 操作者の表示名（日本語名）。op.actorDisplayName へ転写し、サーバが sticky の
   * updatedByName 等へ反映するための情報源。未指定なら省略。
   */
  readonly actorDisplayName?: string | undefined;
  /**
   * 操作者のアバター URL（コメント投稿時の楽観表示用スナップショット）。
   * 最終的な値はサーバが接続時アバターで上書きするため、ここはローカル楽観反映に使うだけ。
   */
  readonly actorAvatarImageUrl?: string | undefined;
  /**
   * 操作者が管理者権限を持つか。コメント編集・削除のクライアント側ガードに使う
   * （ボタン表示の最終判定は UI 側、書き込みの最終判定はサーバ側）。
   */
  readonly actorCanAdmin?: boolean | undefined;
}

// CanvasObject は discriminated union のため、Omit を distributive に展開して
// 個別 type の draft 入力（text / shapeKind / src 等）を保持できるようにする。
type DistributiveDraft<T> = T extends CanvasObject
  ? Omit<T, "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"> & {
      readonly id?: string | undefined;
    }
  : never;

export type CanvasObjectDraft = DistributiveDraft<CanvasObject>;

export interface CanvasOpDispatcher {
  readonly createObject: (object: CanvasObjectDraft) => CanvasObject | null;
  readonly createObjects: (objects: readonly CanvasObjectDraft[]) => readonly CanvasObject[];
  /**
   * 図形と図形を結ぶコネクタ（接続矢印）を作成する。
   * 端点（from / to）の現在位置からルートを算出し、その外接矩形を保存用 x/y/w/h に用いる。
   * 端点オブジェクトが存在しない場合や自己ループの場合は作成せず null を返す。
   */
  readonly createConnector: (
    from: ConnectorEndpoint,
    to: ConnectorEndpoint,
    stroke?: string | undefined
  ) => CanvasObject | null;
  /**
   * 画像・動画の添付を「先に仮表示、あとから本体同期」で配置する。
   *
   * - `object.src` には軽量なプレースホルダ（画像なら超圧縮 LQIP の data URL、
   *   動画なら 1x1 透明 GIF など）を入れて全参加者へ create を即配信する。
   *   blob: URL は他参加者が解決できないため、ここには絶対に入れない。
   * - 作成者本人だけは即フル画質を見られるよう、送信しないローカル専用の
   *   attachment.update（src=localFullSrc=blob URL）を重ねて適用する。
   *
   * 戻り値の overrideOpId は、本体アップロード完了後に
   * {@link finalizeAttachmentPlaceholder} / {@link discardAttachmentPlaceholder} へ渡す。
   */
  readonly createAttachmentPlaceholder: (
    object: CanvasObjectDraft,
    localFullSrc: string
  ) => { readonly object: CanvasObject; readonly overrideOpId: string } | null;
  /**
   * アップロード完了時に呼ぶ。公開 URL を全参加者へ attachment.update で配信し、
   * 本人用ローカル上書き（blob プレビュー）を破棄して全員を公開 URL 表示へ収束させる。
   */
  readonly finalizeAttachmentPlaceholder: (
    objectId: string,
    overrideOpId: string,
    publicSrc: string
  ) => void;
  /**
   * アップロード失敗時に呼ぶ。本人用ローカル上書きを破棄し、仮表示オブジェクトを
   * 全参加者から削除する（壊れた状態を残さない）。
   */
  readonly discardAttachmentPlaceholder: (objectId: string, overrideOpId: string) => void;
  readonly transformObject: (objectId: string, transform: ObjectTransformPatch) => void;
  /**
   * テキスト編集に伴い、図形枠の高さ（h）を本文の実描画高さへ自動で追随させる。
   * キャンバス下端では「現在の y から下端までの残り高さ」で打ち切る（y は動かさない）。
   * ユーザーの明示操作ではない派生的なレイアウト調整のため、Undo 履歴には積まない
   * （入力中の Ctrl+Z は本文編集の取り消しに充てる）。
   *
   * 縮小（h を小さくする）方向は `allowShrink: true` を明示したときだけ反映する。
   * 同時編集では「未送信のローカル変更がある間は各クライアントが自分の本文を保持する」ため、
   * 入力中のライブ実測で縮小まで許すと、短い本文を保持しているクライアントが他者の長い本文の
   * 高さを縮め戻してしまう（last-writer-wins の打ち合いで選択枠やコネクタが暴れる）。
   * よって呼び出し側は、実測が権威本文と対応する場面（編集開始時・編集確定時）だけ
   * allowShrink を立て、入力中のライブ追随は拡張のみとする。
   */
  readonly autoFitObjectHeight: (
    objectId: string,
    h: number,
    options?: { readonly allowShrink?: boolean | undefined } | undefined
  ) => void;
  /**
   * 複数オブジェクトの transform を **1 回の履歴エントリ** にまとめて適用する。
   * グループドラッグなど「ユーザーから見れば 1 操作」を 1 回の Undo で戻せるようにする。
   */
  readonly transformObjects: (
    updates: readonly { readonly objectId: string; readonly transform: ObjectTransformPatch }[]
  ) => void;
  readonly updateText: (objectId: string, text: string) => void;
  /** 色・フォントサイズ・文字寄せ等の見た目だけを変更する。位置・サイズは transformObject 側を使う。 */
  readonly updateObjectStyle: (
    objectId: string,
    style: Partial<{
      readonly color: string;
      readonly fontSize: number;
      readonly fill: string;
      readonly stroke: string;
      readonly textAlign: "left" | "center" | "right";
    }>
  ) => void;
  readonly updateAttachmentSrc: (objectId: string, src: string) => void;
  readonly updateCanvasMeta: (input: {
    title?: string | undefined;
    thumbnailKey?: string | undefined;
    sizePreset?: CanvasSizePreset | undefined;
  }) => void;
  readonly deleteObject: (objectId: string) => void;
  readonly deleteObjects: (objectIds: readonly string[]) => void;
  /**
   * コメントスレッドを新規作成する（起点コメント 1 件を含む comment-thread オブジェクトを配置）。
   * x / y はピンのアンカー（クリック位置・ステージ座標）。images は添付画像の公開 URL（任意）。
   * 本文が空でかつ画像も無いときは作成しない（null を返す）。
   */
  readonly createCommentThread: (input: {
    x: number;
    y: number;
    body: string;
    images?: readonly string[] | undefined;
  }) => CanvasObject | null;
  /** 既存スレッドへ返信メッセージを追加する。本文と画像の両方が空・上限超過時は何もしない。 */
  readonly addCommentMessage: (objectId: string, body: string, images?: readonly string[] | undefined) => void;
  /** スレッド内の 1 メッセージ本文を編集する（投稿者本人 / 管理者のみ。最終判定はサーバ）。 */
  readonly updateCommentMessage: (objectId: string, messageId: string, body: string) => void;
  /** スレッド内の 1 メッセージを削除する（投稿者本人 / 管理者のみ。最終判定はサーバ）。 */
  readonly deleteCommentMessage: (objectId: string, messageId: string) => void;
  readonly undo: () => void;
  readonly redo: () => void;
  readonly commitPenStroke: (
    points: ReadonlyArray<{ x: number; y: number; pressure: number }>,
    color: string,
    thickness: number,
    boundingBox: { x: number; y: number; w: number; h: number },
    /** 描画中 transient と同一の strokeId。確定オブジェクト ID に採用してゴースト破棄を正確化する。 */
    objectId?: string
  ) => PenStrokeCanvasObject | null;
  /**
   * 操作中（未確定）の transform を他参加者へ一時 op として中継する。
   * ゴースト表示用であり永続化しない。ドラッグ移動 / リサイズ / 回転の途中経過に使う。
   */
  readonly previewTransforms: (
    updates: readonly { readonly objectId: string; readonly transform: ObjectTransformPatch }[]
  ) => void;
  /** 描画中ペンストロークの増分点を他参加者へ一時 op として中継する（ゴースト表示用）。 */
  readonly previewPenAppend: (
    strokeId: string,
    points: ReadonlyArray<{ x: number; y: number; pressure: number }>
  ) => void;
  /**
   * テキスト / 付箋を編集中のキャレット位置（本文先頭からの文字オフセット）を他参加者へ
   * 一時 op として中継する（他者のカーソル表示用、永続化しない）。
   */
  readonly previewTextCursor: (objectId: string, cursorIndex: number) => void;
}

export type ObjectTransformPatch = Partial<{
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
  zOrder: number;
  locked: boolean;
}>;

interface HistoryEntry {
  readonly undo: readonly CanvasOp[];
  readonly redo: readonly CanvasOp[];
}

// op に共通して付与するメタ情報（makeMeta の戻り値）。全 op が BaseOp として持つ。
type OpMeta = Pick<
  CanvasOp,
  "clientOpId" | "clientCreatedAt" | "actorUserId" | "actorDisplayName"
>;

const undoStack: HistoryEntry[] = [];
const redoStack: HistoryEntry[] = [];
const HISTORY_LIMIT = 100;

export function clearCanvasHistory(): void {
  undoStack.length = 0;
  redoStack.length = 0;
}

export function useCanvasOpDispatcher(ctx: DispatcherContext): CanvasOpDispatcher {
  const applyOptimistic = useCanvasDocStore((s) => s.applyOptimistic);
  const removeInFlightOp = useCanvasDocStore((s) => s.removeInFlightOp);
  const realtime = useRealtimeClient();
  const showToast = useShowToast();
  const lastSoftLimitToastAtRef = useRef(0);

  const sendOp = useCallback(
    (op: CanvasOp): void => {
      applyOptimistic(op);
      realtime.sendOp(op);
    },
    [applyOptimistic, realtime]
  );

  const dispatch = useCallback(
    (op: CanvasOp, history?: HistoryEntry | undefined): void => {
      if (history) {
        undoStack.push(history);
        if (undoStack.length > HISTORY_LIMIT) undoStack.shift();
        redoStack.length = 0;
      }
      sendOp(op);
    },
    [sendOp]
  );

  const dispatchMany = useCallback(
    (ops: readonly CanvasOp[], history?: HistoryEntry | undefined): void => {
      if (ops.length === 0) return;
      if (history) {
        undoStack.push(history);
        if (undoStack.length > HISTORY_LIMIT) undoStack.shift();
        redoStack.length = 0;
      }
      for (const op of ops) {
        sendOp(op);
      }
    },
    [sendOp]
  );

  const makeMeta = useCallback(
    () => ({
      clientOpId: generateId(),
      clientCreatedAt: new Date().toISOString(),
      actorUserId: ctx.actorUserId,
      // displayName が無い場合はキーごと送らず、旧来 op 形式と差分が出ないようにする。
      ...(ctx.actorDisplayName ? { actorDisplayName: ctx.actorDisplayName } : {}),
    }),
    [ctx.actorUserId, ctx.actorDisplayName]
  );

  const canCreateObjects = useCallback(
    (addCount: number): boolean => {
      const currentCount = Object.keys(useCanvasDocStore.getState().objects).length;
      const nextCount = currentCount + addCount;
      if (nextCount > CANVAS_OBJECT_HARD_LIMIT) {
        showToast(
          "error",
          `このキャンバスはオブジェクト数の上限（${CANVAS_OBJECT_HARD_LIMIT.toLocaleString()}件）に達しています。新しいキャンバスへ分割して作業を続けてください。`
        );
        return false;
      }

      if (nextCount >= CANVAS_OBJECT_SOFT_LIMIT) {
        const now = Date.now();
        if (now - lastSoftLimitToastAtRef.current > 30_000) {
          lastSoftLimitToastAtRef.current = now;
          showToast(
            "warning",
            `このキャンバスのオブジェクト数が多くなっています（${nextCount.toLocaleString()}件）。動作が重い場合は、新しいキャンバスへの分割をご検討ください。`
          );
        }
      }
      return true;
    },
    [showToast]
  );

  // create 用の object と op を組み立てる共通処理。上限チェックを行い、
  // 通過しなければ null を返す（即時 create と「仮表示プレースホルダ」create で共有する）。
  // 当初仕様（キャンバス外にも配置可）へ戻したため、キャンバス範囲外チェックは行わない。
  const buildCreateOp = useCallback(
    (input: CanvasObjectDraft): { object: CanvasObject; op: CanvasOp } | null => {
      if (!canCreateObjects(1)) return null;
      const id = input.id ?? generateId();
      const now = new Date().toISOString();
      const objects = useCanvasDocStore.getState().objects;
      const nextZ = computeNextZOrder(objects);
      const baseObject = clampCanvasObjectToCanvas({
        ...input,
        id,
        version: 1,
        zOrder: nextZ,
        createdAt: now,
        createdBy: ctx.actorUserId,
        updatedAt: now,
        updatedBy: ctx.actorUserId,
      } as CanvasObject);
      // 付箋の初期作成時にも、最終更新者名スナップショットを埋め込む（後続編集と同じ挙動にする）。
      // スタンプは作成者名スナップショットを楽観的に埋め込む（サーバも作成時に同値で厳格上書きする）。
      let object: CanvasObject = baseObject;
      if (ctx.actorDisplayName) {
        if (baseObject.type === "sticky") {
          object = { ...baseObject, updatedByName: ctx.actorDisplayName };
        } else if (baseObject.type === "stamp") {
          object = { ...baseObject, createdByName: ctx.actorDisplayName };
        }
      }
      const op: CanvasOp = {
        type: "object.create",
        clientOpId: generateId(),
        clientCreatedAt: now,
        actorUserId: ctx.actorUserId,
        ...(ctx.actorDisplayName ? { actorDisplayName: ctx.actorDisplayName } : {}),
        object,
      };
      return { object, op };
    },
    [canCreateObjects, ctx.actorUserId, ctx.actorDisplayName]
  );

  const createObject = useCallback<CanvasOpDispatcher["createObject"]>(
    (input) => {
      const built = buildCreateOp(input);
      if (!built) return null;
      dispatch(built.op, {
        undo: [makeDeleteOp(built.object.id, ctx.actorUserId, ctx.actorDisplayName)],
        redo: [built.op],
      });
      return built.object;
    },
    [buildCreateOp, ctx.actorUserId, dispatch]
  );

  const createConnector = useCallback<CanvasOpDispatcher["createConnector"]>(
    (from, to, stroke) => {
      // 自己ループは作らない。
      if (from.objectId === to.objectId) return null;
      const objects = useCanvasDocStore.getState().objects;
      const fromObj = objects[from.objectId];
      const toObj = objects[to.objectId];
      if (!fromObj || !toObj) return null;
      const fromFrame = frameFromObject(fromObj);
      const toFrame = frameFromObject(toObj);
      if (!fromFrame || !toFrame) return null;
      const bbox = connectorStoredFrame(fromFrame, from.side, toFrame, to.side);
      return createObject({
        type: "connector",
        from,
        to,
        x: bbox.x,
        y: bbox.y,
        w: bbox.w,
        h: bbox.h,
        ...(stroke ? { stroke } : {}),
      });
    },
    [createObject]
  );

  const createAttachmentPlaceholder = useCallback<
    CanvasOpDispatcher["createAttachmentPlaceholder"]
  >(
    (input, localFullSrc) => {
      const built = buildCreateOp(input);
      if (!built) return null;
      // 1) プレースホルダ src を載せた create を全参加者へ配信する（peer 側の仮表示）。
      dispatch(built.op, {
        undo: [makeDeleteOp(built.object.id, ctx.actorUserId, ctx.actorDisplayName)],
        redo: [built.op],
      });
      // 2) 作成者本人だけがフル画質を即見られるよう、送信しないローカル専用の
      //    attachment.update を重ねる（inFlightOps に残るため再構築でも維持される）。
      const overrideOp: CanvasOp = {
        type: "object.attachment.update",
        ...makeMeta(),
        objectId: built.object.id,
        src: localFullSrc,
      };
      applyOptimistic(overrideOp);
      return { object: built.object, overrideOpId: overrideOp.clientOpId };
    },
    [buildCreateOp, ctx.actorUserId, dispatch, makeMeta, applyOptimistic]
  );

  const finalizeAttachmentPlaceholder = useCallback<
    CanvasOpDispatcher["finalizeAttachmentPlaceholder"]
  >(
    (objectId, overrideOpId, publicSrc) => {
      // 表示中かどうかに関わらず、まず履歴（undo/redo 両側）の create op の src を公開 URL へ更新する。
      // アップロード完了前に Undo / 削除された場合でも、後続の Redo / Undo で未解決の
      // プレースホルダ（blob:/data:）が復活しないようにするため。
      updateAttachmentCreateHistorySrc(objectId, publicSrc);
      const current = useCanvasDocStore.getState().objects[objectId];
      // すでに削除済み（Undo / 削除済み）なら本人用上書きだけ破棄して終わる。
      if (!current) {
        removeInFlightOp(overrideOpId);
        return;
      }
      // 公開 URL を全参加者へ反映（履歴は create 側の undo=delete に集約するため積まない）。
      const op: CanvasOp = {
        type: "object.attachment.update",
        ...makeMeta(),
        objectId,
        src: publicSrc,
      };
      sendOp(op);
      // 本人用のローカル blob 上書きを破棄し、以後は全員が公開 URL を表示する。
      removeInFlightOp(overrideOpId);
    },
    [makeMeta, sendOp, removeInFlightOp]
  );

  const discardAttachmentPlaceholder = useCallback<
    CanvasOpDispatcher["discardAttachmentPlaceholder"]
  >(
    (objectId, overrideOpId) => {
      removeInFlightOp(overrideOpId);
      // Redo で解決不能なプレースホルダを復活させないよう、履歴エントリも除去する。
      removeAttachmentCreateHistoryEntry(objectId);
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current) return;
      // 仮表示オブジェクトを全参加者から削除し、壊れた状態を残さない。
      sendOp(makeDeleteOp(objectId, ctx.actorUserId, ctx.actorDisplayName));
    },
    [removeInFlightOp, ctx.actorUserId, sendOp]
  );

  const createObjects = useCallback<CanvasOpDispatcher["createObjects"]>(
    (inputs) => {
      if (inputs.length === 0) return [];
      if (!canCreateObjects(inputs.length)) return [];
      const now = new Date().toISOString();
      const objects = useCanvasDocStore.getState().objects;
      let nextZ = computeNextZOrder(objects);
      const created: CanvasObject[] = [];
      for (const input of inputs) {
        const baseObject = clampCanvasObjectToCanvas({
          ...input,
          id: input.id ?? generateId(),
          version: 1,
          // 各反復でも永続化上限へ丸める。computeNextZOrder の初期値だけでなく、複数貼り付け /
          // Alt 複製のループ内 `nextZ += 1` が上限を突き抜けると 2 件目以降の object.create が
          // サーバ検証で reject されて楽観作成が巻き戻るため、都度クランプする。
          zOrder: clampZOrderForPersist(nextZ),
          createdAt: now,
          createdBy: ctx.actorUserId,
          updatedAt: now,
          updatedBy: ctx.actorUserId,
        } as CanvasObject);
        // 単発 createObject（buildCreateOp）と同様に、付箋は最終更新者名、スタンプは作成者名の
        // スナップショットを楽観反映する（サーバも作成時に同値で厳格上書きする）。
        // 複製元の createdByName は cloneObjectAsDraft で除去済みのため、ここで貼り付けた本人の名前になる。
        let object: CanvasObject = baseObject;
        if (ctx.actorDisplayName) {
          if (baseObject.type === "sticky") {
            object = { ...baseObject, updatedByName: ctx.actorDisplayName };
          } else if (baseObject.type === "stamp") {
            object = { ...baseObject, createdByName: ctx.actorDisplayName };
          }
        }
        created.push(object);
        nextZ += 1;
      }
      const ops: CanvasOp[] = created.map((object) => ({
        type: "object.create",
        clientOpId: generateId(),
        clientCreatedAt: now,
        actorUserId: ctx.actorUserId,
        ...(ctx.actorDisplayName ? { actorDisplayName: ctx.actorDisplayName } : {}),
        object,
      }));
      dispatchMany(ops, {
        undo: created.map((object) => makeDeleteOp(object.id, ctx.actorUserId, ctx.actorDisplayName)),
        redo: ops,
      });
      return created;
    },
    [canCreateObjects, ctx.actorUserId, dispatchMany]
  );

  const transformObject = useCallback<CanvasOpDispatcher["transformObject"]>(
    (objectId, transform) => {
      const objects = useCanvasDocStore.getState().objects;
      const current = objects[objectId];
      if (!current) return;
      // ロック中はロック切替（locked のみ）以外の transform を最終防衛として一切送らない。
      // ドラッグ・リサイズ・回転は pointerdown 時の locked チェックを行うが、開始後 pointerup
      // までの間に他クライアントから locked: true が反映されたケースを救う必要がある。
      if (current.locked && !isLockOnlyTransform(transform)) return;
      // zOrder はサーバの永続化上限（±CANVAS_OBJECT_Z_ORDER_LIMIT）へ入口で丸める。
      // 旧スキーマ由来の範囲外 zOrder を持つ既存データがあるキャンバスで、「最前面へ移動」
      // （既存 max+1）等が上限超過の op を送り、楽観更新後にサーバ reject で巻き戻って
      // 「操作が失敗したように見える」のを防ぐ（MCP の採番丸めと同じ方針）。
      const effectiveTransform = clampTransformZOrderForPersist(transform);
      const inverse = pickCurrentTransform(current, effectiveTransform);
      const op: CanvasOp = {
        type: "object.transform",
        ...makeMeta(),
        objectId,
        transform: effectiveTransform,
      };
      // 位置・サイズが変わる場合は、この図形を端点とするコネクタの保存 bbox も同じ履歴に含めて
      // 更新する（描画は端点から都度計算するが、範囲選択・ミニマップ・他参加者選択枠など AABB を
      // 参照する箇所が古い bbox を使わないようにするため）。
      const movedFrame = frameAfterTransform(current, effectiveTransform);
      const connectorUpdates = movedFrame
        ? buildConnectorBboxUpdateOps(objects, new Map([[objectId, movedFrame]]), makeMeta)
        : { ops: [], undo: [] };
      if (connectorUpdates.ops.length > 0) {
        dispatchMany([op, ...connectorUpdates.ops], {
          undo: [
            { type: "object.transform", ...makeMeta(), objectId, transform: inverse },
            ...connectorUpdates.undo,
          ],
          redo: [op, ...connectorUpdates.ops],
        });
        return;
      }
      dispatch(op, {
        undo: [{ type: "object.transform", ...makeMeta(), objectId, transform: inverse }],
        redo: [op],
      });
    },
    [dispatch, dispatchMany, makeMeta]
  );

  // テキスト本文の実描画高さに合わせた図形枠の自動追随（伸縮・履歴なし）。
  // transformObject と異なり Undo 履歴へ積まない: 入力のたびに走る派生的な調整で履歴を
  // 埋めると、Ctrl+Z が「枠の高さ戻し」に消費されて本文編集の取り消しが遠くなるため。
  const autoFitObjectHeight = useCallback<CanvasOpDispatcher["autoFitObjectHeight"]>(
    (objectId, h, options) => {
      const objects = useCanvasDocStore.getState().objects;
      const current = objects[objectId];
      if (!current || current.locked) return;
      // 当初仕様（キャンバス外にも配置可）へ戻したため、キャンバス下端での高さ打ち切りは行わない
      // （下側余白や大サイズ外周にある text / sticky でも自動高さ調整が止まらないようにする）。
      // 実描画高さ h をそのまま用いる（サーバ側も境界クランプを撤廃済みで、確定値と乖離しない）。
      const effectiveH = h;
      // 数値でない・非正はスキップ。1px 未満の差は測定ノイズとみなして op を送らない
      // （複数クライアントの微小な測定差で op を打ち合わないための不感帯）。
      if (!Number.isFinite(effectiveH) || effectiveH <= 0) return;
      if (Math.abs(effectiveH - current.h) < 1) return;
      // 縮小は allowShrink 明示時のみ（同時編集での縮み戻し競合の防止。interface コメント参照）。
      if (effectiveH < current.h && options?.allowShrink !== true) return;
      const op: CanvasOp = {
        type: "object.transform",
        ...makeMeta(),
        objectId,
        transform: { h: effectiveH },
      };
      // サイズが変わるため、この図形を端点とするコネクタの保存 bbox も一緒に更新する
      // （transformObject と同じ理由。範囲選択・ミニマップ等が古い bbox を参照しないようにする）。
      const movedFrame = frameAfterTransform(current, { h: effectiveH });
      const connectorUpdates = movedFrame
        ? buildConnectorBboxUpdateOps(objects, new Map([[objectId, movedFrame]]), makeMeta)
        : { ops: [], undo: [] };
      dispatchMany([op, ...connectorUpdates.ops]);
    },
    [dispatchMany, makeMeta]
  );

  // 複数オブジェクトの transform を 1 回の履歴エントリにまとめる。
  // グループドラッグなど「1 ユーザー操作 = 複数 op」を 1 回の Ctrl+Z で戻せるようにする。
  const transformObjects = useCallback<CanvasOpDispatcher["transformObjects"]>(
    (updates) => {
      if (updates.length === 0) return;
      const objects = useCanvasDocStore.getState().objects;
      const ops: CanvasOp[] = [];
      const undo: CanvasOp[] = [];

      // 位置・サイズが変わった図形の確定後フレーム（コネクタ bbox 再計算用）。
      const movedFrames = new Map<string, ConnectorFrame>();
      const updatedIds = new Set<string>();

      for (const { objectId, transform } of updates) {
        const current = objects[objectId];
        if (!current) continue;
        // 個別 transformObject と同じ locked ガード。グループ内に locked が混ざっていても
        // 個別にスキップして残りは通す（=1 entry にロック解除前の状態を保持できる）。
        if (current.locked && !isLockOnlyTransform(transform)) continue;
        // 個別 transformObject と同じく、zOrder は永続化上限へ入口で丸める。
        const effectiveTransform = clampTransformZOrderForPersist(transform);
        const inverse = pickCurrentTransform(current, effectiveTransform);
        const op: CanvasOp = {
          type: "object.transform",
          ...makeMeta(),
          objectId,
          transform: effectiveTransform,
        };
        ops.push(op);
        undo.push({ type: "object.transform", ...makeMeta(), objectId, transform: inverse });
        updatedIds.add(objectId);
        const movedFrame = frameAfterTransform(current, effectiveTransform);
        if (movedFrame) movedFrames.set(objectId, movedFrame);
      }

      if (ops.length === 0) return;

      // 端点が動いたコネクタの保存 bbox も同じ履歴に含めて更新する（updates 自体に含まれる
      // コネクタは二重更新を避けるため除外する）。
      const connectorUpdates =
        movedFrames.size > 0
          ? buildConnectorBboxUpdateOps(objects, movedFrames, makeMeta, updatedIds)
          : { ops: [], undo: [] };

      dispatchMany([...ops, ...connectorUpdates.ops], {
        undo: [...undo, ...connectorUpdates.undo],
        redo: [...ops, ...connectorUpdates.ops],
      });
    },
    [dispatchMany, makeMeta]
  );

  const updateText = useCallback<CanvasOpDispatcher["updateText"]>(
    (objectId, text) => {
      const current = useCanvasDocStore.getState().objects[objectId];
      // ロック中は本文更新も行わない。useObjectTextEditor 側でも編集モードを抜けて
      // 保留中の debounce timer は破棄しているが、UI イベントや blur のタイミング差で
      // この経路に到達しても契約を守れるよう dispatcher 側でも防御する。
      if (!current || !("text" in current) || current.locked || current.text === text) return;
      const op: CanvasOp = {
        type: "object.text.update",
        ...makeMeta(),
        objectId,
        text,
      };
      dispatch(op, {
        undo: [{ type: "object.text.update", ...makeMeta(), objectId, text: current.text }],
        redo: [op],
      });
    },
    [dispatch, makeMeta]
  );

  const updateObjectStyle = useCallback<CanvasOpDispatcher["updateObjectStyle"]>(
    (objectId, style) => {
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current) return;
      if (current.locked) return;
      // 適用前の各フィールド値を取得し undo の逆 op を組み立てる。対応する field を持たない
      // オブジェクト種別では効果が無いため、style の中で実際に効果のあるフィールドだけ拾う。
      //
      // 注意（undo の制約）: object.style.update の op スキーマは optional な値の「未設定（undefined）」を
      // 明示的に表現できない（key を省略する = 変更しない）。そのため、変更前のフィールド値が
      // undefined だったケースでは、逆 op の対応キーを省略するしかなく、Undo してもフィールド値は
      // 「変更後の値のまま」となる。これは現状の op モデルに起因する既知の制約で、目に見える
      // 退行を起こすケースは「既定値で作成済みのオブジェクトに対する色 / サイズ変更」だけに限られる
      // （新規作成経路では sticky.color, text.fontSize, pen-stroke.color は常に値を持つ）。
      // 将来的に「unset 用センチネル」を導入する場合はここを拡張する。
      const effectiveStyle: Partial<{
        color: string;
        fontSize: number;
        fill: string;
        stroke: string;
        textAlign: "left" | "center" | "right";
      }> = {};
      const inverseStyle: Partial<{
        color: string;
        fontSize: number;
        fill: string;
        stroke: string;
        textAlign: "left" | "center" | "right";
      }> = {};
      // color フィールドを持つオブジェクト種別だけを白リスト化して扱う（image / video / link-card 等は除外）。
      const colorBearingTypes: ReadonlyArray<CanvasObject["type"]> = [
        "sticky",
        "text",
        "pen-stroke",
      ];
      if (style.color !== undefined && colorBearingTypes.includes(current.type)) {
        effectiveStyle.color = style.color;
        const prev = (current as { color?: string | undefined }).color;
        if (prev !== undefined) inverseStyle.color = prev;
      }
      if (style.fontSize !== undefined && current.type === "text") {
        effectiveStyle.fontSize = style.fontSize;
        if (current.fontSize !== undefined) inverseStyle.fontSize = current.fontSize;
      }
      if (style.fill !== undefined && current.type === "shape") {
        effectiveStyle.fill = style.fill;
        // 背景なし（fill 未設定）から色を付けた場合も Undo で背景なしへ戻せるよう、
        // 逆 op には「未設定なら transparent（=塗りなし表示）」を積む（textAlign と同方針）。
        // これをしないと、塗りなし図形へ初回で背景色を付けた操作が Undo で戻らない。
        inverseStyle.fill = current.fill ?? "transparent";
      }
      if (style.fill !== undefined && current.type === "section") {
        effectiveStyle.fill = style.fill;
        // セクションは「背景なし」を持たず未設定 = 既定色表示のため、fill 未設定から初めて
        // 色を付けた場合の逆 op には既定色（SECTION_DEFAULT_FILL）を積んで Undo を成立させる。
        inverseStyle.fill = current.fill ?? SECTION_DEFAULT_FILL;
      }
      if (style.stroke !== undefined && current.type === "shape") {
        effectiveStyle.stroke = style.stroke;
        if (current.stroke !== undefined) inverseStyle.stroke = current.stroke;
      }
      // 文字寄せは text / sticky にのみ適用する。未指定（=左寄せ）から変更する場合は逆 op を
      // 省略するしかなく、Undo で左寄せへ戻らない既知の制約があるため、逆 op が確実に作れる
      // よう「変更前が未指定なら left を逆 op に積む」ことで Undo でも左寄せへ戻せるようにする。
      if (
        style.textAlign !== undefined &&
        (current.type === "text" || current.type === "sticky")
      ) {
        effectiveStyle.textAlign = style.textAlign;
        inverseStyle.textAlign = current.textAlign ?? "left";
      }
      if (Object.keys(effectiveStyle).length === 0) return;
      const op: CanvasOp = {
        type: "object.style.update",
        ...makeMeta(),
        objectId,
        style: effectiveStyle,
      };
      const undoOp: CanvasOp | null =
        Object.keys(inverseStyle).length > 0
          ? {
              type: "object.style.update",
              ...makeMeta(),
              objectId,
              style: inverseStyle,
            }
          : null;
      dispatch(op, {
        undo: undoOp ? [undoOp] : [],
        redo: [op],
      });
    },
    [dispatch, makeMeta]
  );

  const updateAttachmentSrc = useCallback<CanvasOpDispatcher["updateAttachmentSrc"]>(
    (objectId, src) => {
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current) return;
      // ロック中は添付の差し替えも行わない（transformObject / updateText / updateObjectStyle と
      // 同じ最終防衛。サーバ権威側 op-applier も同契約で reject する）。
      // アップロード完了時の確定は履歴を通さない finalizeAttachmentPlaceholder が直接送るため、
      // このガードの影響を受けない。
      if (current.locked) return;
      const previousSrc = getAttachmentSrc(current);
      const op: CanvasOp = {
        type: "object.attachment.update",
        ...makeMeta(),
        objectId,
        src,
      };
      dispatch(op, {
        undo: previousSrc
          ? [{ type: "object.attachment.update", ...makeMeta(), objectId, src: previousSrc }]
          : [],
        redo: [op],
      });
    },
    [dispatch, makeMeta]
  );

  const updateCanvasMeta = useCallback<CanvasOpDispatcher["updateCanvasMeta"]>(
    (input) => {
      const current = useCanvasDocStore.getState().meta;
      const nextTitle = input.title?.trim();
      const op: CanvasOp = {
        type: "canvas.meta.update",
        ...makeMeta(),
        ...(nextTitle ? { title: nextTitle } : {}),
        ...(input.thumbnailKey !== undefined ? { thumbnailKey: input.thumbnailKey } : {}),
        ...(input.sizePreset !== undefined ? { sizePreset: input.sizePreset } : {}),
      };
      if (op.title === undefined && op.thumbnailKey === undefined && op.sizePreset === undefined) {
        return;
      }
      dispatch(op, {
        undo: [
          {
            type: "canvas.meta.update",
            ...makeMeta(),
            ...(op.title !== undefined ? { title: current.title } : {}),
            ...(op.thumbnailKey !== undefined && current.thumbnailKey !== undefined
              ? { thumbnailKey: current.thumbnailKey }
              : {}),
            // サイズ変更の undo は変更前のプリセット（未設定なら既定=中）へ戻す。
            ...(op.sizePreset !== undefined
              ? { sizePreset: normalizeCanvasSizePreset(current.sizePreset) }
              : {}),
          },
        ],
        redo: [op],
      });
    },
    [dispatch, makeMeta]
  );

  const deleteObjects = useCallback<CanvasOpDispatcher["deleteObjects"]>(
    (objectIds) => {
      if (objectIds.length === 0) return;
      const objects = useCanvasDocStore.getState().objects;
      // 削除対象の図形を接続先とするコネクタも巻き込んで削除する（宙ぶらりんな矢印を残さない）。
      const requested = new Set(objectIds);
      const connectorIds = collectConnectorCascadeDeleteIds(Object.values(objects), requested);
      const allIds = [...objectIds, ...connectorIds];
      const targets = allIds.map((id) => objects[id]).filter((o): o is CanvasObject => Boolean(o));
      if (targets.length === 0) return;
      // 削除も「端点図形が先・コネクタが後」の順に送る。サーバはロック中コネクタの削除を
      // 「端点が既に存在しない（＝カスケード相当）」場合だけ例外的に許可するため、選択順が
      // 「コネクタ → 端点」だとコネクタの delete が先に届いて reject されてしまう
      //（ロックしていないコネクタでは順序に依存しないが、契約を 1 つに保つため常にこの順序で送る）。
      const orderedTargets = [
        ...targets.filter((object) => object.type !== "connector"),
        ...targets.filter((object) => object.type === "connector"),
      ];
      const ops = orderedTargets.map((object) =>
        makeDeleteOp(object.id, ctx.actorUserId, ctx.actorDisplayName)
      );
      // Undo の復元（object.create）でも、サーバの connector 端点検証に通るよう端点図形を先に、
      // コネクタを後に作成する。選択順が「コネクタ → 端点」のときに Undo でコネクタ create が
      // 端点より先に送られて reject され、矢印だけ復元されない事態を防ぐ（作成順の不変条件を
      // 削除 Undo の復元経路にも適用する）。
      dispatchMany(ops, {
        undo: orderedTargets.map((object) =>
          makeCreateOp(object, ctx.actorUserId, ctx.actorDisplayName)
        ),
        redo: ops,
      });
    },
    [ctx.actorUserId, ctx.actorDisplayName, dispatchMany]
  );

  const deleteObject = useCallback<CanvasOpDispatcher["deleteObject"]>(
    (objectId) => {
      const objects = useCanvasDocStore.getState().objects;
      const current = objects[objectId];
      if (!current) return;
      // 接続先として参照しているコネクタも一緒に削除し、宙ぶらりんな矢印を残さない。
      // 1 回の履歴エントリにまとめ、Undo で図形とコネクタがそろって復元されるようにする。
      const connectorIds = collectConnectorCascadeDeleteIds(Object.values(objects), new Set([objectId]));
      if (connectorIds.length > 0) {
        deleteObjects([objectId, ...connectorIds]);
        return;
      }
      const op = makeDeleteOp(objectId, ctx.actorUserId, ctx.actorDisplayName);
      dispatch(op, {
        undo: [makeCreateOp(current, ctx.actorUserId, ctx.actorDisplayName)],
        redo: [op],
      });
    },
    [ctx.actorUserId, ctx.actorDisplayName, dispatch, deleteObjects]
  );

  const commitPenStroke = useCallback<CanvasOpDispatcher["commitPenStroke"]>(
    (points, color, thickness, _boundingBox, objectId) => {
      if (!canCreateObjects(1)) return null;
      const id = objectId ?? generateId();
      const now = new Date().toISOString();
      const objects = useCanvasDocStore.getState().objects;
      const nextZ = computeNextZOrder(objects);
      const clippedPoints = points.map((point) => clampPointToCanvas(point));
      const boundingBox = computePenStrokeFrame(clippedPoints, thickness);
      const stroke: PenStrokeCanvasObject = {
        type: "pen-stroke",
        id,
        x: boundingBox.x,
        y: boundingBox.y,
        w: boundingBox.w,
        h: boundingBox.h,
        zOrder: nextZ,
        version: 1,
        color,
        thickness,
        points: clippedPoints,
        createdAt: now,
        createdBy: ctx.actorUserId,
        updatedAt: now,
        updatedBy: ctx.actorUserId,
      };
      const op: CanvasOp = {
        type: "pen.stroke.commit",
        clientOpId: generateId(),
        clientCreatedAt: now,
        actorUserId: ctx.actorUserId,
        ...(ctx.actorDisplayName ? { actorDisplayName: ctx.actorDisplayName } : {}),
        object: stroke,
      };
      dispatch(op, {
        undo: [makeDeleteOp(stroke.id, ctx.actorUserId, ctx.actorDisplayName)],
        redo: [op],
      });
      return stroke;
    },
    [canCreateObjects, ctx.actorUserId, ctx.actorDisplayName, dispatch]
  );

  const undo = useCallback((): void => {
    const entry = undoStack.pop();
    if (!entry) return;
    // ロック衝突がある場合は履歴に戻して何もしない（再ロック解除後は再度 Ctrl+Z 可能）。
    // dispatcher 側（transformObject / updateText / updateObjectStyle / updateAttachmentSrc /
    // 削除ショートカット）の locked ガードを履歴経路でも迂回されないようにする。
    // 対象 op 種別は hasLockedObjectMutationConflict を参照。
    if (hasLockedObjectMutationConflict(entry.undo)) {
      undoStack.push(entry);
      return;
    }
    for (const op of entry.undo) {
      sendOp(refreshOpMeta(op, ctx.actorUserId, ctx.actorDisplayName));
    }
    redoStack.push(entry);
  }, [ctx.actorUserId, ctx.actorDisplayName, sendOp]);

  const redo = useCallback((): void => {
    const entry = redoStack.pop();
    if (!entry) return;
    if (hasLockedObjectMutationConflict(entry.redo)) {
      redoStack.push(entry);
      return;
    }
    for (const op of entry.redo) {
      sendOp(refreshOpMeta(op, ctx.actorUserId, ctx.actorDisplayName));
    }
    undoStack.push(entry);
  }, [ctx.actorUserId, ctx.actorDisplayName, sendOp]);

  const previewTransforms = useCallback<CanvasOpDispatcher["previewTransforms"]>(
    (updates) => {
      for (const { objectId, transform } of updates) {
        const sanitized = pickTransientTransform(transform);
        if (Object.keys(sanitized).length === 0) continue;
        realtime.sendMessage({
          type: "op.transient",
          op: {
            type: "object.transient.transform",
            clientOpId: generateId(),
            objectId,
            transform: sanitized,
          },
        });
      }
    },
    [realtime]
  );

  const previewPenAppend = useCallback<CanvasOpDispatcher["previewPenAppend"]>(
    (strokeId, points) => {
      if (points.length === 0) return;
      realtime.sendMessage({
        type: "op.transient",
        op: {
          type: "pen.stroke.append",
          clientOpId: generateId(),
          strokeId,
          points: points.map((p) => ({ x: p.x, y: p.y, pressure: p.pressure })),
        },
      });
    },
    [realtime]
  );

  const previewTextCursor = useCallback<CanvasOpDispatcher["previewTextCursor"]>(
    (objectId, cursorIndex) => {
      // サーバ側スキーマ（0..8192 の整数）に合わせて丸める。負値や NaN は 0 にする。
      const index = Number.isFinite(cursorIndex)
        ? Math.max(0, Math.min(8192, Math.round(cursorIndex)))
        : 0;
      realtime.sendMessage({
        type: "op.transient",
        op: {
          type: "object.text.cursor",
          clientOpId: generateId(),
          objectId,
          cursorIndex: index,
        },
      });
    },
    [realtime]
  );

  // 楽観表示用のローカルなコメントメッセージを組み立てる。投稿者情報はサーバが接続時の
  // 認可済み値で上書きするため、ここでは自分自身のスナップショットを載せるだけ。
  const buildLocalCommentMessage = useCallback(
    (body: string, images?: readonly string[] | undefined): CommentMessage => {
      const now = new Date().toISOString();
      return {
        id: generateId(),
        authorId: ctx.actorUserId,
        authorName: ctx.actorDisplayName ?? "",
        ...(ctx.actorAvatarImageUrl ? { authorAvatar: ctx.actorAvatarImageUrl } : {}),
        body,
        // 添付画像（アップロード済み公開 URL）。空配列は載せない（サーバスキーマと表示分岐を単純にする）。
        ...(images && images.length > 0 ? { images: [...images] } : {}),
        createdAt: now,
        updatedAt: now,
      };
    },
    [ctx.actorUserId, ctx.actorDisplayName, ctx.actorAvatarImageUrl]
  );

  const createCommentThread = useCallback<CanvasOpDispatcher["createCommentThread"]>(
    (input) => {
      // 本文が空でも画像があれば投稿できる（画像だけのコメントを許容する）。
      if (input.body.trim().length === 0 && (input.images?.length ?? 0) === 0) return null;
      const message = buildLocalCommentMessage(input.body, input.images);
      // 上限・範囲チェックは buildCreateOp で共通化しつつ、コメント配置は Undo 履歴へ積まない。
      // createObject 経由（Undo=object.delete）にすると、配置後に他者が返信したスレッドを
      // 作成者の Ctrl+Z が丸ごと削除して他者コメントを巻き込む / Redo で古い単一メッセージ状態が
      // 復活する、といった履歴破綻が起きるため。返信・編集・削除と同様に非 Undo とする。
      const built = buildCreateOp({
        type: "comment-thread",
        x: input.x,
        y: input.y,
        w: COMMENT_THREAD_NOMINAL_SIZE,
        h: COMMENT_THREAD_NOMINAL_SIZE,
        messages: [message],
      });
      if (!built) return null;
      dispatch(built.op);
      return built.object;
    },
    [buildLocalCommentMessage, buildCreateOp, dispatch]
  );

  const addCommentMessage = useCallback<CanvasOpDispatcher["addCommentMessage"]>(
    (objectId, body, images) => {
      // 本文が空でも画像があれば投稿できる（画像だけの返信を許容する）。
      if (body.trim().length === 0 && (images?.length ?? 0) === 0) return;
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current || current.type !== "comment-thread") return;
      if (current.messages.length >= COMMENT_MESSAGES_MAX) {
        showToast(
          "warning",
          `1 つのコメントスレッドに追加できる件数の上限（${COMMENT_MESSAGES_MAX}件）に達しています。`
        );
        return;
      }
      const op: CanvasOp = {
        type: "comment.message.add",
        ...makeMeta(),
        objectId,
        message: buildLocalCommentMessage(body, images),
      };
      // コメントのメッセージ追加・編集・削除はキャンバスの Undo 履歴へ積まない（本文編集に近い操作のため）。
      dispatch(op);
    },
    [buildLocalCommentMessage, dispatch, makeMeta, showToast]
  );

  const updateCommentMessage = useCallback<CanvasOpDispatcher["updateCommentMessage"]>(
    (objectId, messageId, body) => {
      if (body.trim().length === 0) return;
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current || current.type !== "comment-thread") return;
      const target = current.messages.find((m) => m.id === messageId);
      // クライアント側ガード（最終判定はサーバ）: 本人または管理者でなければ送らない。
      if (!target || (target.authorId !== ctx.actorUserId && ctx.actorCanAdmin !== true)) return;
      if (target.body === body) return;
      const op: CanvasOp = {
        type: "comment.message.update",
        ...makeMeta(),
        objectId,
        messageId,
        body,
      };
      dispatch(op);
    },
    [ctx.actorUserId, ctx.actorCanAdmin, dispatch, makeMeta]
  );

  const deleteCommentMessage = useCallback<CanvasOpDispatcher["deleteCommentMessage"]>(
    (objectId, messageId) => {
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current || current.type !== "comment-thread") return;
      const target = current.messages.find((m) => m.id === messageId);
      if (!target || (target.authorId !== ctx.actorUserId && ctx.actorCanAdmin !== true)) return;
      const op: CanvasOp = {
        type: "comment.message.delete",
        ...makeMeta(),
        objectId,
        messageId,
      };
      dispatch(op);
    },
    [ctx.actorUserId, ctx.actorCanAdmin, dispatch, makeMeta]
  );

  // dispatcher の identity を安定化する（メンバは全て useCallback 済み）。
  // 毎レンダーで新しいオブジェクトを返すと、dispatcher を effect 依存に持つドラッグ系フック
  // （useResizeHandle / useRotationHandle / useObjectDrag）が provider の再レンダーのたびに
  // リスナーを再バインドし、ドラッグ中だと進行中のドラッグ状態（pointerup での確定）を失って
  // 「プレビューだけ残って確定 op が送られない＝枠線とつまみがサイズ変更へ追従しない」実障害の
  // 引き金になっていた。
  return useMemo(
    () => ({
      createObject,
      createObjects,
      createConnector,
      createAttachmentPlaceholder,
      finalizeAttachmentPlaceholder,
      discardAttachmentPlaceholder,
      transformObject,
      autoFitObjectHeight,
      transformObjects,
      updateText,
      updateObjectStyle,
      updateAttachmentSrc,
      updateCanvasMeta,
      deleteObject,
      deleteObjects,
      createCommentThread,
      addCommentMessage,
      updateCommentMessage,
      deleteCommentMessage,
      undo,
      redo,
      commitPenStroke,
      previewTransforms,
      previewPenAppend,
      previewTextCursor,
    }),
    [
      createObject,
      createObjects,
      createConnector,
      createAttachmentPlaceholder,
      finalizeAttachmentPlaceholder,
      discardAttachmentPlaceholder,
      transformObject,
      autoFitObjectHeight,
      transformObjects,
      updateText,
      updateObjectStyle,
      updateAttachmentSrc,
      updateCanvasMeta,
      deleteObject,
      deleteObjects,
      createCommentThread,
      addCommentMessage,
      updateCommentMessage,
      deleteCommentMessage,
      undo,
      redo,
      commitPenStroke,
      previewTransforms,
      previewPenAppend,
      previewTextCursor,
    ]
  );
}

// 一時 transform op が許容するフィールド（x/y/w/h/rotation）だけを取り出す。
// zOrder / locked などの永続専用フィールドはゴーストプレビューでは扱わない。
function pickTransientTransform(
  transform: ObjectTransformPatch
): Partial<{ x: number; y: number; w: number; h: number; rotation: number }> {
  const picked: Partial<{ x: number; y: number; w: number; h: number; rotation: number }> = {};
  if (transform.x !== undefined) picked.x = transform.x;
  if (transform.y !== undefined) picked.y = transform.y;
  if (transform.w !== undefined) picked.w = transform.w;
  if (transform.h !== undefined) picked.h = transform.h;
  if (transform.rotation !== undefined) picked.rotation = transform.rotation;
  return picked;
}

// 仮表示 create の履歴エントリ（undo / redo 両側の object.create）の src を、アップロード完了後の
// 確定 src（公開 URL）に更新する。これをしないと、
//  - Undo -> Redo で redo 側がプレースホルダ付き create を復元する
//  - アップロード完了前の通常削除で undo 側に保存された blob:/data: が復元される
// といった経路で、アップロードは済んでいるのに永続的に「読み込み中」表示になる。
// このため undo / redo の両側を書き換える。
function updateAttachmentCreateHistorySrc(objectId: string, src: string): void {
  for (const stack of [undoStack, redoStack]) {
    for (let i = 0; i < stack.length; i++) {
      const entry = stack[i];
      if (!entry) continue;
      let changed = false;
      const rewriteCreateSrc = (op: CanvasOp): CanvasOp => {
        if (
          op.type === "object.create" &&
          op.object.id === objectId &&
          (op.object.type === "image" || op.object.type === "video")
        ) {
          changed = true;
          return { ...op, object: { ...op.object, src } } as CanvasOp;
        }
        return op;
      };
      const undo = entry.undo.map(rewriteCreateSrc);
      const redo = entry.redo.map(rewriteCreateSrc);
      if (changed) stack[i] = { undo, redo };
    }
  }
}

// 仮表示 create の履歴エントリを除去する。アップロード失敗時に呼び、二度と解決できない
// 壊れたプレースホルダを Undo / Redo で復活させないようにする。
// create op は履歴エントリの redo 側（新規作成）だけでなく、アップロード完了前に通常削除した
// 場合の undo 側（makeCreateOp による復元用）にも積まれるため、undo / redo の両側を対象にする。
function removeAttachmentCreateHistoryEntry(objectId: string): void {
  const hasCreate = (entry: HistoryEntry): boolean =>
    [...entry.undo, ...entry.redo].some(
      (op) => op.type === "object.create" && op.object.id === objectId
    );
  for (const stack of [undoStack, redoStack]) {
    for (let i = stack.length - 1; i >= 0; i--) {
      const entry = stack[i];
      if (entry && hasCreate(entry)) stack.splice(i, 1);
    }
  }
}

function makeCreateOp(
  object: CanvasObject,
  actorUserId: string,
  actorDisplayName?: string | undefined
): CanvasOp {
  return {
    type: "object.create",
    clientOpId: generateId(),
    clientCreatedAt: new Date().toISOString(),
    actorUserId,
    ...(actorDisplayName ? { actorDisplayName } : {}),
    object,
  };
}

function makeDeleteOp(
  objectId: string,
  actorUserId: string,
  actorDisplayName?: string | undefined
): CanvasOp {
  return {
    type: "object.delete",
    clientOpId: generateId(),
    clientCreatedAt: new Date().toISOString(),
    actorUserId,
    ...(actorDisplayName ? { actorDisplayName } : {}),
    objectId,
  };
}

function refreshOpMeta(
  op: CanvasOp,
  actorUserId: string,
  actorDisplayName: string | undefined
): CanvasOp {
  return {
    ...op,
    clientOpId: generateId(),
    clientCreatedAt: new Date().toISOString(),
    actorUserId,
    // undo/redo の実行時点での displayName で上書きする（再生時の名前が記録時点と乖離しないように）。
    ...(actorDisplayName ? { actorDisplayName } : {}),
  } as CanvasOp;
}

// transform が「locked のみで構成される＝ロック切替」かを判定する。
// ロック中でもロック切替（特に解除）の経路は通したいので、ガード判定の鍵となる。
function isLockOnlyTransform(transform: ObjectTransformPatch): boolean {
  const transformKeys = Object.keys(transform);
  return transformKeys.length > 0 && transformKeys.every((key) => key === "locked");
}

// undo / redo に積まれた op 集合のうち、ロック中オブジェクトに対する
// 「編集系」op が含まれるかを判定する。
// 対象は次の 5 種類:
//  - object.transform: 位置・サイズ・回転・zOrder などの変形（ロック切替単独だけは許可）
//  - object.style.update: 色・フォントサイズ・塗り・線の見た目変更（常にロックで抑止）
//  - object.text.update: 本文の変更（常にロックで抑止）
//  - object.attachment.update: 画像 / 動画 / リンクカードの差し替え（常にロックで抑止）
//  - object.delete: 削除（端点削除に伴う connector のカスケードだけは例外的に許可）
// 含まれている場合、そのエントリは履歴に戻して送信スキップする。
// 対象種別はサーバ権威側のガード（op-applier の assertNotLockedObjectMutation /
// assertLockedDeleteAllowed）と同じ契約にそろえ、履歴経路の op がサーバで reject されて
// 楽観更新だけが巻き戻る状態を避ける。
function hasLockedObjectMutationConflict(ops: readonly CanvasOp[]): boolean {
  // 同じ履歴エントリ内で削除されるオブジェクト id（connector のカスケード判定に使う）。
  const deletedIds = new Set(
    ops.filter((op) => op.type === "object.delete").map((op) => op.objectId)
  );
  return ops.some((op) => {
    if (
      op.type !== "object.transform" &&
      op.type !== "object.style.update" &&
      op.type !== "object.text.update" &&
      op.type !== "object.attachment.update" &&
      op.type !== "object.delete"
    ) {
      return false;
    }
    const current = useCanvasDocStore.getState().objects[op.objectId];
    if (current?.locked !== true) return false;
    if (op.type === "object.transform") return !isLockOnlyTransform(op.transform);
    if (op.type === "object.delete") {
      // 端点図形の削除に巻き込まれる connector の削除は、ロック中でもサーバが例外的に許可する
      // （端点を失った connector は描画も選択もできない不可視データとして残るため）。
      // 同じ履歴エントリで端点も削除される場合だけ、その例外に合わせて衝突扱いしない。
      if (current.type !== "connector") return true;
      return !deletedIds.has(current.from.objectId) && !deletedIds.has(current.to.objectId);
    }
    // style / text / attachment の各更新はロック切替のような例外経路を持たないため、
    // ロック中なら常に衝突扱い。
    return true;
  });
}

function pickCurrentTransform(
  object: CanvasObject,
  requested: ObjectTransformPatch
): ObjectTransformPatch {
  const inverse: ObjectTransformPatch = {};
  if (requested.x !== undefined) inverse.x = object.x;
  if (requested.y !== undefined) inverse.y = object.y;
  if (requested.w !== undefined) inverse.w = object.w;
  if (requested.h !== undefined) inverse.h = object.h;
  if (requested.rotation !== undefined) inverse.rotation = object.rotation ?? 0;
  // undo（元の zOrder へ戻す op）も永続化上限へ丸める。旧スキーマ由来の範囲外 zOrder
  // （例: 5,000,000）を持つ既存データを前面 / 背面移動したあと Ctrl+Z すると、この inverse が
  // object.transform として送られて TransformSchema の上限で reject されるため。丸めると
  // 範囲外の元値へは厳密には戻らないが、上限値へ収束する（可用性優先。作成側の採番と同方針）。
  if (requested.zOrder !== undefined) inverse.zOrder = clampZOrderForPersist(object.zOrder);
  if (requested.locked !== undefined) inverse.locked = object.locked ?? false;
  return inverse;
}

function getAttachmentSrc(object: CanvasObject): string | undefined {
  if (object.type === "image" || object.type === "video") return object.src;
  if (object.type === "link-card") return object.imageUrl;
  return undefined;
}

/**
 * transform 適用後の図形フレーム（x/y/w/h/回転）を返す。位置・サイズ・回転のいずれも変化しない
 * （zOrder / locked のみの）transform や、コネクタ自身の場合は null を返す。
 * 回転はアンカー位置（回転後の辺中点）を変えるため、回転のみの変更でも端点コネクタの
 * 保存 bbox を再計算する必要がある。
 */
function frameAfterTransform(
  current: CanvasObject,
  transform: ObjectTransformPatch
): ConnectorFrame | null {
  if (current.type === "connector") return null;
  const geometryChanged =
    transform.x !== undefined ||
    transform.y !== undefined ||
    transform.w !== undefined ||
    transform.h !== undefined ||
    transform.rotation !== undefined;
  if (!geometryChanged) return null;
  return {
    x: transform.x ?? current.x,
    y: transform.y ?? current.y,
    w: transform.w ?? current.w,
    h: transform.h ?? current.h,
    rotation: transform.rotation ?? current.rotation ?? 0,
  };
}

/**
 * 端点が動いたコネクタの保存 bbox（x/y/w/h）を最新ルートに合わせて更新する transform op 群を返す。
 * movedFrames は「移動後フレーム」を持つ図形 id → frame。未移動の端点は objects の現在値を使う。
 * excludeIds に含まれるコネクタ id は対象外（呼び出し側で別途 transform 済みの場合に二重更新を防ぐ）。
 * bbox が変化しないコネクタは op を生成しない。
 */
function buildConnectorBboxUpdateOps(
  objects: Record<string, CanvasObject>,
  movedFrames: ReadonlyMap<string, ConnectorFrame>,
  makeMeta: () => OpMeta,
  excludeIds?: ReadonlySet<string>
): { ops: CanvasOp[]; undo: CanvasOp[] } {
  const ops: CanvasOp[] = [];
  const undo: CanvasOp[] = [];
  for (const object of Object.values(objects)) {
    if (object.type !== "connector") continue;
    if (excludeIds?.has(object.id)) continue;
    if (!movedFrames.has(object.from.objectId) && !movedFrames.has(object.to.objectId)) continue;
    const fromFrame = movedFrames.get(object.from.objectId) ?? frameFromObject(objects[object.from.objectId]);
    const toFrame = movedFrames.get(object.to.objectId) ?? frameFromObject(objects[object.to.objectId]);
    if (!fromFrame || !toFrame) continue;
    const bbox = connectorStoredFrame(fromFrame, object.from.side, toFrame, object.to.side);
    if (bbox.x === object.x && bbox.y === object.y && bbox.w === object.w && bbox.h === object.h) {
      continue;
    }
    ops.push({
      type: "object.transform",
      ...makeMeta(),
      objectId: object.id,
      transform: { x: bbox.x, y: bbox.y, w: bbox.w, h: bbox.h },
    });
    undo.push({
      type: "object.transform",
      ...makeMeta(),
      objectId: object.id,
      transform: { x: object.x, y: object.y, w: object.w, h: object.h },
    });
  }
  return { ops, undo };
}

/** オブジェクトから接続用フレーム（x/y/w/h/回転）を取り出す。存在しなければ null。 */
function frameFromObject(object: CanvasObject | undefined): ConnectorFrame | null {
  if (!object) return null;
  return { x: object.x, y: object.y, w: object.w, h: object.h, rotation: object.rotation ?? 0 };
}

/**
 * 既存オブジェクトの最大 zOrder + 1 を返す（簡易な「最前面」割り当て）。
 * 旧スキーマ由来の範囲外 zOrder が既存 max のキャンバスでも、新規作成がサーバの
 * 永続化制約（±CANVAS_OBJECT_Z_ORDER_LIMIT）で reject されないよう上限へ丸める。
 */
function computeNextZOrder(objects: Record<string, CanvasObject>): number {
  let max = 0;
  for (const obj of Object.values(objects)) {
    if (obj.zOrder > max) max = obj.zOrder;
  }
  return clampZOrderForPersist(max + 1);
}

/**
 * transform パッチの zOrder をサーバの永続化上限へ丸める（zOrder 以外は変更しない）。
 * 「最前面へ移動」（既存 max+1）/「最背面へ移動」（既存 min-1）が、旧スキーマ由来の
 * 範囲外 zOrder を持つ既存データを起点に上限超過の op を送ってしまうのを入口で防ぐ。
 */
function clampTransformZOrderForPersist(
  transform: TransformObjectOp["transform"]
): TransformObjectOp["transform"] {
  if (transform.zOrder === undefined) return transform;
  return { ...transform, zOrder: clampZOrderForPersist(transform.zOrder) };
}

// 補助型: createObject に渡す引数を 1 種類ずつ作るためのヘルパー。
export type StickyDraftInput = Omit<
  StickyCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type TextDraftInput = Omit<
  TextCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type ShapeDraftInput = Omit<
  ShapeCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type ImageDraftInput = Omit<
  ImageCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type VideoDraftInput = Omit<
  VideoCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type LinkCardDraftInput = Omit<
  LinkCardCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
